import { type FormEvent, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next/pages';
import {
  CountryCode,
  type InputAccessRequest,
  type InputAccessRequestPreferredLanguage,
  type RestrictedListing,
  MarketCountryCode,
} from '@pkgs/api';
import { useSession } from '@ev-tech/user-account-modules';
import { Textarea } from '@ev-tech/ui-core-components/Textarea';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';

import { extractFormData } from '@/utils/formUtils';
import { type Country, getLocalizedCountryList } from '@/utils/countries';
import { mapRequestAccessFormDataToRequestData } from '@/utils/contactForm';
import { sendRequestAccessFormService } from '@/services/sendRequestAccessFormData';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useSettings } from '@/hooks/useSettings';
import { useExposeTracking } from '@/hooks/useExposeTracking';
import { useExposePageProps } from '@/hooks/useExposePageProps';

import { type RequestAccessFormData } from './RequestAccessForm.types';
import { PrivacyStatement } from '../ContactForm/components/PrivacyStatement';
import { PhoneInput } from '../ContactForm/components/PhoneInput';
import { LastNameInput } from '../ContactForm/components/LastNameInput';
import { type ContactFormStatus } from '../ContactForm/components/FormStatusDisplay';
import { FirstNameInput } from '../ContactForm/components/FirstNameInput';
import { EmailInput } from '../ContactForm/components/EmailInput';
import { ConsentLabel } from '../ContactForm/components/ConsentLabel';

import styles from './RequestAccessForm.module.css';

const FormStatusDisplay = dynamic(() => import('../ContactForm/components/FormStatusDisplay').then((mod) => mod.FormStatusDisplay));

export const RequestAccessForm = ({ restrictedListing }: { restrictedListing: RestrictedListing }) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.private_office);
  const { t: tProperty_expose } = useTranslation(TRANSLATION_NAMESPACE.property_expose);

  const { language, countryCode } = useSettings();
  const formRef = useRef<HTMLFormElement>(null);
  const consentRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<ContactFormStatus>('idle');
  const requestData = useRef<InputAccessRequest | null>(null);
  const [countryList, setCountryList] = useState<Country[]>([]);
  const { session } = useSession();
  const initialCountry = countryCode === MarketCountryCode['private-office'] ? CountryCode.DE : countryCode;
  const { pageTrackingTypes } = useExposePageProps();
  const { trackExposeLeadEvent } = useExposeTracking();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // check if form is valid and scroll to first invalid input
    if (!formRef.current?.checkValidity()) {
      const firstInvalidInput = formRef.current?.querySelector<HTMLInputElement>('input:invalid');
      firstInvalidInput?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      firstInvalidInput?.focus({ preventScroll: true });
      return;
    }

    const formData = extractFormData<RequestAccessFormData>(formRef.current);

    // map country code to country name
    if (formData.country) {
      formData.country = countryList.find((c) => c.code === formData.country)?.name || formData.country;
    }

    formData.consentText = consentRef.current?.innerText;

    requestData.current = mapRequestAccessFormDataToRequestData(formData, language.toUpperCase() as InputAccessRequestPreferredLanguage);

    setStatus('processing');
  };

  const sendFormRequest = async (solution: string) => {
    if (status !== 'processing' || !requestData.current) return;

    const result = await sendRequestAccessFormService(
      // oxlint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      restrictedListing.id,
      requestData.current,
      solution,
    );

    setStatus(result.success ? 'success' : 'error');

    if (result.success) {
      trackExposeLeadEvent({
        requestData: requestData.current,
        source: 'request-expose-embedded-form',
      });
      // trigger GTM event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'lead_created',
        source: 'new-search_expose_contact-form',
        flow: 'buyer',
        intention: 'buy',
        ...pageTrackingTypes,
        email: session?.user.emailAddresses[0]?.emailAddress,
      });
    }
  };

  useEffect(() => {
    // set country list only on client side, because there is a mismatch between
    // Node.js and browser Intl.DisplayNames results
    // https://git.engelvoelkers.com/ev/search/frontend/-/merge_requests/1548#note_432251

    setCountryList(getLocalizedCountryList(language));
  }, [language]);

  return (
    <>
      {status === 'idle' ? (
        <div className={styles.embeddedFormWrapper}>
          <form className={styles.form} data-testid="request-access-form" ref={formRef} onSubmit={handleSubmit} noValidate>
            <div className={styles.titleWrapper}>
              <Headline className={styles.headline} size="h4" label={t('search.request.access.form.title')} />
              <Copy className={styles.subtitleCopy} text={t('search.request.access.form.subtitle')} />
            </div>
            <fieldset className={styles.inputGrid}>
              <FirstNameInput />
              <LastNameInput />
              <EmailInput />
              <PhoneInput initialCountry={initialCountry} isInModal={false} />
            </fieldset>
            <Textarea
              label={tProperty_expose('search.contact.form.label.yourMessage')}
              id="requestAccessFormMessage"
              name="message"
              placeholder={t('search.contact.form.label.yourMessage.placeHolder')}
            />
            <ConsentLabel showsPrivacyStatement={false} consentRef={consentRef} />
            <Button
              className={styles.button}
              type="submit"
              label={t('search.request.access.form.button.send')}
              testId="search-components_expose-request-access-form_submit-button"
            />
            <PrivacyStatement countryAlpha2={restrictedListing.countryAlpha2} />
          </form>
        </div>
      ) : (
        <FormStatusDisplay
          email={requestData.current?.email}
          status={status}
          onRetry={() => setStatus('idle')}
          onSolution={sendFormRequest}
        />
      )}
    </>
  );
};
